import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { EyeOff, Eye, Loader2, Search, Users2, X } from "lucide-react";
import { useI18n, serverError } from "../i18n";

/**
 * 案件管理
 * ═══════════════════════════════════════════════════════════════
 *
 * 這一頁存在的理由是「詐騙案件要能在十分鐘內下架」。
 *
 * 下架**不是刪除**：提案、對話、合約全部保留，因為那些同時是
 * 接案者那一邊的紀錄。被下架的案件從公開搜尋與詳情頁消失，
 * 但發案方自己看得到，而且會看到理由。
 *
 * 下架強制填理由 —— 事後一定會有人問「為什麼」，
 * 而「我記得好像是詐騙」不是一個可以拿出來的答案。
 */

interface AdminJob {
  id: string;
  slug: string;
  market: string;
  title: string;
  description: string;
  clientId: string;
  categorySlug: string;
  status: string;
  budgetMinMinor: number;
  budgetMaxMinor: number;
  proposalCount: number;
  viewCount: number;
  postedAt: string;
  removedAt: string | null;
  removedReason: string | null;
}

/* 模組層存 key，不存文字 —— 求值的時候語系還不存在 */
const FILTERS = [
  { value: "", key: "jobs.all" },
  { value: "open", key: "jobs.open" },
  { value: "awarded", key: "jobs.awarded" },
  { value: "completed", key: "jobs.completed" },
  { value: "removed", key: "jobs.removed" },
] as const;

export default function Jobs() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState<{ items: AdminJob[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: "50" });
      if (q.trim()) params.set("q", q.trim());
      if (status) params.set("status", status);

      const res = await fetch(`/api/jobs?${params}`, { credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setData(payload.data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  // 搜尋要 debounce —— 每打一個字就查一次，資料庫會被打爛
  useEffect(() => {
    const timer = setTimeout(() => void load(), q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, q]);

  return (
    <div>
      <div className="flex items-center gap-2.5 rounded-2xl nm-inset px-4 py-3">
        <Search className="size-4 shrink-0 text-ink-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("jobs.search")}
          className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
        />
        {q && (
          <button type="button" onClick={() => setQ("")} aria-label={t("jobs.clear")}>
            <X className="size-3.5 text-ink-muted" />
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatus(f.value)}
            className={
              "rounded-pill px-4 py-2 text-xs font-medium transition-all " +
              (status === f.value ? "nm-inset text-ink" : "nm-pressable text-ink-soft")
            }
          >
            {t(f.key)}
          </button>
        ))}
      </div>

      {error && <p className="mt-5 rounded-card nm-inset p-5 text-sm text-brand-300">{error}</p>}

      <p className="mt-5 text-xs text-ink-muted">
        {loading ? t("jobs.loading") : t("jobs.total", { n: data?.total ?? 0 })}
      </p>

      <div className="mt-3 space-y-2.5">
        {loading && !data
          ? [0, 1, 2].map((i) => <div key={i} className="h-28 rounded-card shimmer" />)
          : (data?.items ?? []).map((job) => (
              <JobRow key={job.id} job={job} onChange={load} />
            ))}

        {!loading && (data?.items ?? []).length === 0 && (
          <p className="rounded-card nm-inset p-8 text-center text-sm text-ink-muted">
            {t("jobs.none")}
          </p>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, onChange }: { job: AdminJob; onChange: () => void }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const removed = Boolean(job.removedAt);

  async function moderate(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/jobs/${job.id}/moderate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ removed: next, reason: reason.trim() || undefined }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setReason("");
      setOpen(false);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={"rounded-card p-5 " + (removed ? "nm-inset" : "nm")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{job.title}</span>
            <span className="rounded-pill nm-inset-sm px-2 py-0.5 text-[10px] text-ink-muted">
              {job.market}
            </span>
          </div>
          <p className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-muted">
            <span>{job.categorySlug}</span>
            <span>
              ${Math.round(job.budgetMinMinor / 100)}–${Math.round(job.budgetMaxMinor / 100)}
            </span>
            <span className="flex items-center gap-1">
              <Users2 className="size-3" />
              {t("jobs.proposals", { n: job.proposalCount })}
            </span>
            <span>{t("jobs.views", { n: job.viewCount })}</span>
            <span>{new Date(job.postedAt).toLocaleDateString(locale)}</span>
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {removed ? (
            <span className="flex items-center gap-1.5 rounded-pill bg-brand-600/26 px-2.5 py-1 text-[10px] font-medium text-brand-100">
              <EyeOff className="size-3" />
              {t("jobs.removed")}
            </span>
          ) : (
            <span className="rounded-pill nm-sm px-2.5 py-1 text-[10px] font-medium text-ink-soft">
              {job.status === "open"
                ? t("jobs.open")
                : job.status === "awarded"
                  ? t("jobs.awarded")
                  : job.status}
            </span>
          )}
        </div>
      </div>

      <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-ink-soft">{job.description}</p>

      {removed && job.removedReason && (
        <p className="mt-3 rounded-xl nm-sm px-3.5 py-2.5 text-[11px] leading-relaxed text-ink-soft">
          {t("jobs.removedReason", { reason: job.removedReason })}
        </p>
      )}

      {error && <p className="mt-3 text-[11px] text-brand-300">{error}</p>}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-4 rounded-xl nm-pressable px-4 py-2 text-xs font-medium text-ink-soft"
        aria-expanded={open}
      >
        {open ? t("jobs.collapse") : removed ? t("jobs.restore") : t("jobs.takeDown")}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-2xl nm-inset p-4">
              <p className="text-[11px] leading-relaxed text-ink-muted">
                {removed ? t("jobs.restoreNote") : t("jobs.takeDownNote")}
              </p>

              {!removed && (
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("jobs.reasonPlaceholder")}
                  className="mt-3 w-full rounded-xl nm-inset-sm bg-transparent px-3.5 py-2.5 text-xs text-ink outline-none placeholder:text-ink-muted"
                />
              )}

              <button
                type="button"
                disabled={busy || (!removed && !reason.trim())}
                onClick={() => void moderate(!removed)}
                className="mt-3 flex items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-[11px] font-medium text-ink disabled:opacity-40"
              >
                {busy ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : removed ? (
                  <Eye className="size-3" />
                ) : (
                  <EyeOff className="size-3" />
                )}
                {removed ? t("jobs.restoreAction") : t("jobs.takeDownAction")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
